import { useNavigate } from "react-router-dom";
import { ClipboardCheck, BarChart3, Shield, Users, Play, Info, ChevronDown, HelpCircle, FileText } from "lucide-react";
import { useState } from "react";

const faqData = [
  {
    question: "O que são riscos psicossociais no trabalho?",
    answer: "Riscos psicossociais são fatores relacionados à organização do trabalho e às relações interpessoais que podem afetar a saúde mental e física dos trabalhadores. Incluem aspectos como sobrecarga de trabalho, falta de autonomia, conflitos interpessoais, assédio moral, insegurança no emprego e desequilíbrio entre vida pessoal e profissional."
  },
  {
    question: "O que é a NR-01 e qual sua relação com riscos psicossociais?",
    answer: "A NR-01 (Norma Regulamentadora nº 01) estabelece disposições gerais sobre segurança e saúde no trabalho. Com as atualizações recentes, passou a exigir que as empresas incluam os riscos psicossociais no Programa de Gerenciamento de Riscos (PGR), tornando obrigatória a identificação, avaliação e controle desses riscos."
  },
  {
    question: "Como funciona a avaliação COPSOQ?",
    answer: "O COPSOQ (Copenhagen Psychosocial Questionnaire) é um instrumento científico validado internacionalmente para avaliar riscos psicossociais. Nossa versão curta contém 20 questões que avaliam 5 dimensões principais: Exigências do Trabalho, Relações Sociais e Liderança, Autonomia, Sentido do Trabalho e Insegurança/Conflitos."
  },
  {
    question: "Quem deve responder o questionário?",
    answer: "Todos os colaboradores da empresa devem ser convidados a participar. A participação é voluntária e anônima. Quanto maior a adesão, mais representativos serão os resultados para a elaboração de ações preventivas eficazes."
  },
  {
    question: "Quanto tempo leva para responder?",
    answer: "O questionário leva aproximadamente 5 a 10 minutos para ser respondido. As perguntas são objetivas e de múltipla escolha, facilitando o preenchimento rápido e preciso."
  },
  {
    question: "As respostas são anônimas e confidenciais?",
    answer: "Sim, absolutamente. O sistema não coleta dados que permitam identificar o respondente individualmente. Os resultados são apresentados de forma agregada por setor/função, garantindo total anonimato e sigilo das respostas."
  },
  {
    question: "O que acontece após a avaliação?",
    answer: "Os resultados são compilados em um relatório técnico que identifica os principais fatores de risco por setor e função. Com base nesses dados, a empresa pode elaborar um plano de ação para mitigar os riscos identificados, incluindo as medidas no PGR conforme exigido pela NR-01."
  },
  {
    question: "Com que frequência a avaliação deve ser realizada?",
    answer: "Recomenda-se realizar a avaliação periodicamente, pelo menos uma vez ao ano, ou sempre que houver mudanças significativas na organização do trabalho. Isso permite monitorar a evolução dos indicadores e a eficácia das medidas implementadas."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full py-4 px-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-medium text-gray-900 pr-4">{question}</span>
      <ChevronDown 
        className={`w-5 h-5 text-orange-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
      />
    </button>
    <div 
      className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 pb-4' : 'max-h-0'}`}
    >
      <p className="px-4 text-gray-600 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const Landing = () => {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 flex flex-col">
      <div className="flex-1 max-w-7xl mx-auto px-6 py-4 md:py-6">
        {/* Header with Logo aligned to badge */}
        <header className="text-center mb-8 md:mb-10 animate-fade-in relative">
          {/* Logo positioned to the left on desktop */}
          <div className="md:absolute md:left-0 md:top-0 flex justify-center md:justify-start mb-4 md:mb-0">
            <div className="bg-gradient-to-br from-orange-50 via-orange-50/80 to-orange-100/50 rounded-xl p-3 inline-block">
              <img 
                src="/viaseg-logo.jpeg" 
                alt="ViaSeg - Assessoria em Medicina Ocupacional" 
                className="h-16 md:h-20 lg:h-24 w-auto object-contain mix-blend-multiply"
                data-testid="viaseg-logo"
              />
            </div>
          </div>

          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-5">
            <Shield className="w-4 h-4" />
            Conformidade NR-01
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-4">
            Avaliação de Riscos
            <br />
            <span className="gradient-text">Psicossociais</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Sistema completo para identificação e gestão de riscos psicossociais conforme a nova NR-01, baseado no modelo COPSOQ.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto mb-8 md:mb-10">
          <div
            data-testid="respond-questionnaire-card"
            onClick={() => navigate("/questionnaire")}
            className="bg-white border-2 border-orange-200 rounded-2xl md:rounded-3xl p-6 md:p-8 card-hover cursor-pointer group"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-100 rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-4 group-hover:bg-orange-500 group-hover:scale-110 transition-all duration-300">
              <ClipboardCheck className="w-6 h-6 md:w-7 md:h-7 text-orange-600 group-hover:text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">Responder Questionário</h2>
            <p className="text-gray-600 leading-relaxed mb-3 md:mb-4 text-sm md:text-base">
              Complete a avaliação psicossocial com 20 questões baseadas no COPSOQ versão curta.
            </p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center text-orange-600 font-semibold group-hover:translate-x-2 transition-transform text-sm md:text-base">
                Iniciar avaliação
                <span className="ml-2">→</span>
              </div>
              <a
                data-testid="access-guide-btn"
                href="/guia-acesso-viaseg-nr01.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 px-3 py-2 bg-orange-50 hover:bg-orange-500 hover:text-white text-orange-700 text-xs md:text-sm font-semibold rounded-lg border border-orange-200 hover:border-orange-500 transition-all duration-200 shadow-sm hover:shadow-md no-underline"
                title="Abrir Guia de aplicação Viaseg NR-01 (PDF)"
              >
                <FileText className="w-4 h-4" />
                Guia de acesso
              </a>
            </div>
          </div>

          <div
            data-testid="dashboard-card"
            onClick={() => navigate("/login")}
            className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl md:rounded-3xl p-6 md:p-8 card-hover cursor-pointer group"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-4 group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
              <BarChart3 className="w-6 h-6 md:w-7 md:h-7 text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">Dashboard Administrativo</h2>
            <p className="text-orange-50 leading-relaxed mb-3 md:mb-4 text-sm md:text-base">
              Visualize resultados, estatísticas e exporte dados das avaliações realizadas.
            </p>
            <div className="flex items-center font-semibold group-hover:translate-x-2 transition-transform text-sm md:text-base">
              Acessar dashboard
              <span className="ml-2">→</span>
            </div>
          </div>
        </div>

        {/* Video Explicativo Section */}
        <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-indigo-50/30 rounded-2xl md:rounded-3xl p-6 md:p-8 mb-8 md:mb-10 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Info className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900">O que são Riscos Psicossociais?</h3>
              <p className="text-sm text-gray-500">Assista ao vídeo explicativo</p>
            </div>
          </div>
          
          {!showVideo ? (
            <div 
              onClick={() => setShowVideo(true)}
              className="relative cursor-pointer group rounded-xl overflow-hidden bg-gradient-to-br from-indigo-900 to-purple-900 aspect-video"
              data-testid="video-thumbnail"
            >
              {/* Thumbnail overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-indigo-600 ml-1" fill="currentColor" />
                </div>
              </div>
              {/* Text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white font-medium text-sm md:text-base">Clique para assistir a explicação sobre identificação de riscos psicossociais</p>
                <p className="text-white/70 text-xs md:text-sm mt-1">Duração: ~2 minutos</p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden bg-black aspect-video">
              <video 
                controls 
                autoPlay 
                className="w-full h-full"
                data-testid="video-player"
              >
                <source src="/video-explicativo.mp4" type="video/mp4" />
                Seu navegador não suporta a reprodução de vídeos.
              </video>
            </div>
          )}
          
          <p className="text-sm text-gray-500 mt-4 text-center">
            Este vídeo explica como são identificados e avaliados os riscos psicossociais no ambiente de trabalho conforme a NR-01.
          </p>
        </div>

        <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg shadow-orange-100/50">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 text-center">5 Dimensões Avaliadas</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            <div className="text-center p-3 md:p-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3">
                <span className="text-lg md:text-2xl font-bold text-orange-600">1</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">Exigências do Trabalho</h4>
              <p className="text-xs text-gray-600 hidden md:block">Ritmo, carga e demandas</p>
            </div>
            <div className="text-center p-3 md:p-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3">
                <span className="text-lg md:text-2xl font-bold text-orange-600">2</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">Relações Sociais</h4>
              <p className="text-xs text-gray-600 hidden md:block">Liderança e apoio</p>
            </div>
            <div className="text-center p-3 md:p-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3">
                <span className="text-lg md:text-2xl font-bold text-orange-600">3</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">Autonomia</h4>
              <p className="text-xs text-gray-600 hidden md:block">Controle e decisão</p>
            </div>
            <div className="text-center p-3 md:p-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3">
                <span className="text-lg md:text-2xl font-bold text-orange-600">4</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">Sentido do Trabalho</h4>
              <p className="text-xs text-gray-600 hidden md:block">Propósito e valor</p>
            </div>
            <div className="text-center p-3 md:p-4 col-span-2 md:col-span-1">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3">
                <span className="text-lg md:text-2xl font-bold text-orange-600">5</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm">Insegurança</h4>
              <p className="text-xs text-gray-600 hidden md:block">Conflitos e estabilidade</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg shadow-orange-100/50 mt-8 md:mt-10">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900">Perguntas Frequentes</h3>
          </div>
          
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-xl overflow-hidden">
            {faqData.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
          
          <p className="text-center text-sm text-gray-500 mt-6">
            Tem outras dúvidas? Entre em contato com a equipe ViaSeg para mais informações.
          </p>
        </div>

        <footer className="text-center mt-6 md:mt-10 text-gray-500 text-xs md:text-sm space-y-1 md:space-y-2">
          <p>Sistema de Avaliação NR-01 • COPSOQ Versão Curta • Conformidade MTE</p>
          <p className="text-gray-400">2026 - ViaSeg Assessoria em Medicina Ocupacional e Segurança do Trabalho</p>
        </footer>
      </div>
    </div>
  );
};

export default Landing;